<template>
  <div class="content-copy">
    <el-card class="copy-card">
      <div class="copy-header">
        <div class="content-info" v-if="!isBundle">
          <img
            v-if="contentInfo.cover_url"
            :src="contentInfo.cover_url"
            class="content-cover"
            alt="Content cover"
          />
          <div class="content-details">
            <span class="content-type" :class="contentInfo.type">
              {{ contentInfo.type }}
            </span>
            <h2>{{ contentInfo.title }}</h2>
            <div class="club-name">For {{ club_name }} ({{ account_name }})</div>
            <div class="copy-stats">
              <div class="stat-item">
                <span class="label">Total Copies</span>
                <span class="value">{{ copyStats.total_count }}</span>
              </div>
              <div class="stat-item">
                <span class="label">Available Copies</span>
                <span class="value">{{ copyStats.available_count }}</span>
              </div>
              <div class="stat-item">
                <span class="label">Used Copies</span>
                <span class="value">{{ copyStats.used_count }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="content-info" v-else>
          <div class="content-details">
            <span class="content-type bundle">Bundle</span>
            <h2>{{ bundleInfo.bundle_name }}</h2>
            <div class="club-name">For {{ club_name }} ({{ account_name }})</div>
            <p class="bundle-description">{{ bundleInfo.bundle_description }}</p>
            <div class="bundle-collections-list">
              <div v-for="(col, colId) in bundleInfo.collection_array" :key="colId" class="bundle-collection-item">
                <img v-if="col.cover_url" :src="col.cover_url" class="collection-thumb" />
                <span>{{ col.title }}</span>
              </div>
            </div>
            <div class="copy-stats">
              <div class="stat-item">
                <span class="label">Total Bundles</span>
                <span class="value">{{ bundleCopyStats.total_per_collection || 0 }}</span>
              </div>
              <div class="stat-item">
                <span class="label">Available Bundles</span>
                <span class="value">{{ bundleCopyStats.bundle_available || 0 }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="header-actions">
          <el-button
            type="primary"
            @click="handleShareCopies"
            v-if="userType === 'advisor'"
          >
            Share Copies to Students
          </el-button>
          <el-button
            type="primary"
            @click="handleGetCopy"
            v-if="!userType || userType === 'student'"
          >
            {{ isBundle ? 'Get Bundle Copy' : 'Get A Copy' }}
          </el-button>
        </div>
      </div>

      <!-- Add description section for non-logged in users -->
      <div v-if="!userType" class="content-description">
        <h3>Description</h3>
        <p>{{ contentInfo.description || 'No description available.' }}</p>
        <el-alert
          title="Please log in to access this content"
          type="info"
          show-icon
          :closable="false"
          class="login-alert"
        >
          <template slot="title">
            Please <el-button type="text" @click="goToLogin">log in</el-button> to access this content
          </template>
        </el-alert>
      </div>

      <el-tabs v-model="activeTab" v-if="userType === 'advisor'">
        

        <el-tab-pane label="Copy List" name="consumption">
          <el-table :data="consumptionHistory" style="width: 100%">
            <el-table-column prop="_id.$id" label="Copy ID" width="240">
            </el-table-column>
            <el-table-column prop="student_email" label="Student">
              <template slot-scope="scope">
                <span v-if="scope.row.status === 'USED'">{{ scope.row.student_name || scope.row.student_email }}</span>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column prop="status" label="Status" width="120">
              <template slot-scope="scope">
                <el-tag :type="getStatusType(scope.row.status)">
                  {{ scope.row.status }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="claim_time" label="Claim Time" width="240">
              <template slot-scope="scope">
                <span v-if="scope.row.status === 'USED'">{{ formatDate(scope.row.claim_time * 1000) }}</span>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column label="Operations" width="120" align="center">
              <template slot-scope="scope">
                <el-button
                  v-if="scope.row.status === 'AVAILABLE'"
                  type="primary"
                  size="small"
                  @click="assignCopy(scope.row)"
                >
                  ASSIGN
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="Order History" name="orders">
          <el-table :data="orderHistory" style="width: 100%">
            <el-table-column prop="_id.$id" label="Order ID" width="240">
            </el-table-column>
            <el-table-column 
              prop="contact_name" 
              label="Ordered By" 
              width="120"
              class-name="no-wrap-column">
            </el-table-column>
            <el-table-column prop="quantity" label="Copies" width="100">
            </el-table-column>
            <el-table-column prop="total_price" label="Amount" width="120">
              <template slot-scope="scope">
                ${{ scope.row.total_price }}
              </template>
            </el-table-column>
            
            <el-table-column prop="order_time" label="Date" width="240">
              <template slot-scope="scope">
                {{ formatDate(scope.row.order_time *1000) }}
              </template>
            </el-table-column>
            
            <el-table-column prop="status" label="Status" width="120">
              <template slot-scope="scope">
                <el-tag :type="getStatusType(scope.row.status)">
                  {{ scope.row.status }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="Actions" width="120" align="center">
              <template slot-scope="scope">
                <el-button
                  v-if="scope.row.status === 'PENDING'"
                  type="primary"
                  size="small"
                  @click="goToPayment(scope.row)"
                >
                  Pay Now
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <el-dialog
      title="Share Copies"
      :visible.sync="shareDialogVisible"
      width="600px"
    >
      <div class="share-content">
        <div class="share-notes">
          <h4>Important Notes:</h4>
          <ul>
            <li>This link allows students to access and claim available copies</li>
            <li>Only students in your club can use this link</li>
            <li>The link will remain active as long as there are available copies</li>
          </ul>
        </div>
        <div class="share-url">
          <div class="url-label">Share URL:</div>
          <div class="url-input-group">
            <el-input
              v-model="shareUrl"
              readonly
              size="medium"
            ></el-input>
            <el-button
              type="primary"
              size="medium"
              @click="copyShareUrl"
            >
              Copy URL
            </el-button>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- Add new login dialog -->
    <el-dialog
      title="Login"
      :visible.sync="loginDialogVisible"
      width="400px"
      :close-on-click-modal="false"
    >
      <login-simple @login-success="handleLoginSuccess" />
    </el-dialog>
  </div>
</template>

<script>
import { postRequest } from '@/api/eclub'
import { getUserId, getUserType } from '@/utils/store'
import LoginSimple from '@/components/LoginSimple'

export default {
  name: 'ContentCopy',
  components: {
    LoginSimple
  },
  data() {
    return {
      isAdvisor: false,
      contentInfo: {},
      bundleInfo: {},
      bundleCopyStats: {},
      copyStats: {
        total_copies: 0,
        available_copies: 0,
        used_copies: 0
      },
      activeTab: 'consumption',
      orderHistory: [],
      consumptionHistory: [],
      club_name: '',
      account_name: '',
      userType: '',
      shareDialogVisible: false,
      shareUrl: '',
      loginDialogVisible: false,
      queryParams: {},
    }
  },
  computed: {
    isBundle() {
      return !!this.$route.query.bundle_id
    }
  },
  created() {
    this.userType = getUserType()
    this.club_name = this.$route.query.club_name
    this.queryParams = { ...this.$route.query }
    if (this.isBundle) {
      this.fetchBundleInfo()
      this.fetchBundleCopyStats()
    } else {
      this.fetchContentInfo()
      this.fetchCopyStats()
    }
    this.fetchCopies()
    this.fetchOrderHistory()
    this.getClubName()
  },
  methods: {
    async checkUserRole() {
      
    },
    async fetchBundleInfo() {
      try {
        const response = await postRequest('getCollectionBundle', {
          bundle_id: this.$route.query.bundle_id
        })
        if (response.data.code === 0) {
          this.bundleInfo = response.data.data
        }
      } catch (error) {
        console.error('Error fetching bundle info:', error)
      }
    },
    async fetchBundleCopyStats() {
      try {
        const response = await postRequest('getBundleCopyStats', {
          bundle_id: this.$route.query.bundle_id,
          club_id: this.$route.query.club_id
        })
        if (response.data.code === 0) {
          this.bundleCopyStats = response.data.data
        }
      } catch (error) {
        console.error('Error fetching bundle copy stats:', error)
      }
    },
    async fetchContentInfo() {
      try {
        const response = await postRequest('getStoreContent', {
          content_id: this.$route.query.content_id,
        })
        if (response.data.code === 0) {
          this.contentInfo = response.data.data
        }
      } catch (error) {
        console.error('Error fetching content info:', error)
      }
    },
    async fetchCopyStats() {
      try {
        const response = await postRequest('getContentCopyStats', {
          content_id: this.$route.query.content_id,
          club_id: this.$route.query.club_id
        })
        if (response.data.code === 0) {
          this.copyStats = response.data.data
        }
      } catch (error) {
        console.error('Error fetching copy stats:', error)
      }
    },
    async fetchCopies() {
      try {
        const response = await postRequest('getContentCopies', {
          content_id: this.$route.query.content_id,
          
          club_id: this.$route.query.club_id
        })
        if (response.data.code === 0) {
          this.consumptionHistory = response.data.data
        }
      } catch (error) {
        console.error('Error fetching order history:', error)
      }
    },
    async fetchOrderHistory() {
      try {
        const response = await postRequest('getContentOrderHistory', {
          content_id: this.$route.query.content_id,
          club_id: this.$route.query.club_id
        })
        if (response.data.code === 0) {
          this.orderHistory = response.data.data
        }
      } catch (error) {
        console.error('Error fetching order history:', error)
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return '-'
      return new Date(timestamp).toLocaleString()
    },
    getStatusType(status) {
      const types = {
        PENDING: 'warning',
        COMPLETED: 'success',
        FAILED: 'danger',
        AVAILABLE: 'success'
      }
      return types[status] || 'info'
    },
    goToPayment(order) {
      this.$router.push({
        path: '/payment',
        query: {
          order_id: order._id.$id,
          content_id: this.$route.query.content_id,
          club_id: this.$route.query.club_id
        }
      })
    },
    async assignCopy(copy) {
      try {
        const response = await postRequest('assignContentCopy', {
          copy_id: copy._id.$id,
          content_id: this.$route.query.content_id,
          club_id: this.$route.query.club_id
        })
        if (response.data.code === 0) {
          this.$message.success('Copy assigned successfully')
          this.fetchCopies() // Refresh the copy list
        }
      } catch (error) {
        console.error('Error assigning copy:', error)
        this.$message.error('Failed to assign copy')
      }
    },
    async getClubName() {
      try {
        const response = await postRequest('getClubName', {
          club_id: this.$route.query.club_id
        })
        if (response.data.code === 0) {
          this.club_name = response.data.data.name
          this.account_name = response.data.data.account_name
        }
      } catch (error) {
        console.error('Error fetching club name:', error)
      }
    },
    handleShareCopies() {
      if (this.isBundle) {
        this.shareUrl = `${window.location.origin}/#/home/contentCopy?bundle_id=${this.$route.query.bundle_id}&club_id=${this.$route.query.club_id}`
      } else {
        this.shareUrl = `${window.location.origin}/#/home/contentCopy?content_id=${this.$route.query.content_id}&club_id=${this.$route.query.club_id}`
      }
      this.shareDialogVisible = true
    },
    
    async copyShareUrl() {
      try {
        await navigator.clipboard.writeText(this.shareUrl)
        this.$message.success('URL copied to clipboard')
      } catch (err) {
        this.$message.error('Failed to copy URL')
        console.error('Failed to copy URL:', err)
      }
    },
    goToLogin() {
      this.loginDialogVisible = true
    },
    async handleLoginSuccess() {
      this.loginDialogVisible = false
      this.userType = getUserType()
      await this.$root.$emit('user-logged-in', true)
      await this.$nextTick()

      if (this.isBundle) {
        await Promise.all([
          this.fetchBundleInfo(),
          this.fetchBundleCopyStats(),
        ])
      } else {
        await Promise.all([
          this.fetchContentInfo(),
          this.fetchCopyStats(),
          this.fetchCopies(),
          this.fetchOrderHistory()
        ])
      }

      if (this.$route.path === '/home/contentCopy') {
        this.handleGetCopy()
      }
    },
    handleGetCopy() {
      if (!this.userType) {
        this.goToLogin()
        return
      }
      const confirmMsg = this.isBundle
        ? 'Are you sure you want to get a copy of this bundle? This will claim all collections in the bundle.'
        : 'Are you sure you want to get a copy of this content?'
      this.$confirm(confirmMsg, 'Confirm', {
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        type: 'info'
      }).then(async () => {
        try {
          let response
          if (this.isBundle) {
            response = await postRequest('claimBundleCopyByStudent', {
              student_id: getUserId(),
              club_id: this.$route.query.club_id,
              bundle_id: this.$route.query.bundle_id
            })
          } else {
            response = await postRequest('claimCopyByStudent', {
              student_id: getUserId(),
              club_id: this.$route.query.club_id,
              content_id: this.$route.query.content_id
            })
          }

          if (response.data.code === 0) {
            this.$message.success(this.isBundle ? 'Successfully claimed bundle' : 'Successfully claimed a copy')
            if (this.isBundle) {
              this.$router.push({
                name: 'Bundle Detail',
                query: { bundle_id: this.$route.query.bundle_id }
              })
            } else {
              this.$router.push({
                path: '/home/contentProfile',
                query: { content_id: this.$route.query.content_id }
              })
            }
          } else {
            this.$message.error(response.data.msg || 'Failed to claim copy')
          }
        } catch (error) {
          console.error('Error claiming copy:', error)
          this.$message.error('Failed to claim copy')
        }
      }).catch(() => {
        // User clicked No, do nothing
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.content-copy {
  padding: 20px;

  .copy-card {
    .copy-header {
      margin-bottom: 24px;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;

      .content-info {
        display: flex;
        gap: 20px;
        margin-bottom: 24px;

        .content-cover {
          width: 120px;
          height: 168px;
          object-fit: cover;
          border-radius: 8px;
        }

        .content-details {
          display: flex;
          flex-direction: column;
          justify-content: center;

          .club-name {
            font-size: 14px;
            color: #606266;
            margin-bottom: 12px;
            font-weight: 500;
          }

          .content-type {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 16px;
            font-size: 13px;
            margin-bottom: 12px;
            text-transform: capitalize;
            font-weight: 500;

            &.events {
              background-color: #e1f3d8;
              color: #67c23a;
            }

            &.posts {
              background-color: #fdf6ec;
              color: #e6a23c;
            }
          }

          h2 {
            margin: 0 0 8px 0;
            color: #303133;
            font-size: 24px;
            line-height: 1.4;
            font-weight: 600;
          }

          .bundle-description {
            color: #606266;
            font-size: 14px;
            margin: 8px 0 16px;
            line-height: 1.5;
          }

          .bundle-collections-list {
            display: flex;
            gap: 16px;
            flex-wrap: wrap;
            margin-bottom: 16px;

            .bundle-collection-item {
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 6px 12px;
              background: #f5f7fa;
              border-radius: 6px;
              font-size: 13px;
              color: #303133;

              .collection-thumb {
                width: 32px;
                height: 32px;
                border-radius: 4px;
                object-fit: cover;
              }
            }
          }
        }
      }

      .content-type.bundle {
        background-color: #ecf5ff;
        color: #409EFF;
      }

      .copy-stats {
        display: flex;
        gap: 40px;
        padding: 20px;
        background-color: #f5f7fa;
        border-radius: 8px;

        .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;

          .label {
            font-size: 14px;
            color: #909399;
            margin-bottom: 4px;
          }

          .value {
            font-size: 24px;
            font-weight: 500;
            color: #303133;
          }
        }
      }

      .header-actions {
        margin-left: 20px;
      }
    }
  }
}

:deep(.no-wrap-column) {
  white-space: nowrap;
}

.share-content {
  .share-notes {
    margin-bottom: 24px;

    h4 {
      margin: 0 0 12px;
      color: #303133;
    }

    ul {
      margin: 0;
      padding-left: 20px;
      color: #606266;

      li {
        margin-bottom: 8px;
      }
    }
  }

  .share-url {
    .url-label {
      font-weight: 500;
      margin-bottom: 8px;
      color: #303133;
    }

    .url-input-group {
      display: flex;
      gap: 12px;

      .el-input {
        flex: 1;
      }
    }
  }
}

.content-description {
  padding: 20px 0;

  h3 {
    margin: 0 0 16px;
    color: #303133;
  }

  p {
    color: #606266;
    line-height: 1.6;
    margin: 0 0 20px;
  }

  .login-alert {
    margin-top: 20px;
  }
}
</style> 